import { useEffect, useRef, useState } from 'react';
import { Participant } from 'twilio-video';
import useParticipantNetworkQualityLevel from '../useParticipantNetworkQualityLevel/useParticipantNetworkQualityLevel';
import useVideoContext from '../useVideoContext/useVideoContext';

interface ConnectionWarningOptions {
  participant: Participant;
  isLocal?: boolean;
}

const STATS_POLL_INTERVAL_MS = 1000;
const MAX_NETWORK_QUALITY_LEVEL = 2;
const CONSECUTIVE_BAD_READS_TO_SHOW = 2;

function isLocalTrackBlacked(track: any): boolean {
  if (!track) return true;
  return track.bytesSent === 0 || track.bytesSent === null;
}

function isRemoteTrackBlacked(track: any): boolean {
  if (!track) return true;
  return track.timestamp === 0 || (track.bytesReceived === null && track.packetsReceived === null);
}

export default function useParticipantConnectionWarning({
  participant,
  isLocal: isLocalProp,
}: ConnectionWarningOptions) {
  const { room } = useVideoContext();

  const isLocal = room?.localParticipant ? room.localParticipant.sid === participant?.sid : isLocalProp ?? false;

  const networkQualityLevel = useParticipantNetworkQualityLevel(participant);
  const [hasBlackScreen, setHasBlackScreen] = useState(false);
  const consecutiveBadReadsRef = useRef(0);

  useEffect(() => {
    if (!room || !participant) return;

    let statsInterval: ReturnType<typeof setInterval> | null = null;

    const startupTimeout = setTimeout(() => {
      statsInterval = setInterval(async () => {
        let isIssueDetected = false;

        try {
          const statsReports = await room.getStats();

          statsReports.forEach((report: any) => {
            const participantTrackSids = new Set<string>();

            participant.tracks.forEach(pub => {
              if (pub.trackSid) {
                participantTrackSids.add(pub.trackSid);
              }
            });

            const audioTrack = report.remoteAudioTrackStats?.find(
              (t: any) => t.trackSid && participantTrackSids.has(t.trackSid)
            );

            const videoTrack = report.remoteVideoTrackStats?.find(
              (t: any) => t.trackSid && participantTrackSids.has(t.trackSid)
            );

            const checks: boolean[] = [];

            if (audioTrack) {
              checks.push(isRemoteTrackBlacked(audioTrack));
            }

            if (videoTrack) {
              checks.push(isRemoteTrackBlacked(videoTrack));
            }

            if (checks.length > 0 && checks.every(Boolean)) {
              isIssueDetected = true;
            }
          });
        } catch (err) {
          console.warn('[useParticipantConnectionWarning] getStats falhou', err);
        }

        if (isIssueDetected) {
          consecutiveBadReadsRef.current += 1;
          if (consecutiveBadReadsRef.current >= CONSECUTIVE_BAD_READS_TO_SHOW) {
            setHasBlackScreen(true);
          }
        } else {
          consecutiveBadReadsRef.current = 0;
          setHasBlackScreen(false);
        }
      }, STATS_POLL_INTERVAL_MS);
    }, 4000);

    return () => {
      clearTimeout(startupTimeout);
      if (statsInterval) clearInterval(statsInterval);
      consecutiveBadReadsRef.current = 0;
    };
  }, [room, isLocal, participant]);

  const isQualityLevelLow = networkQualityLevel !== null && networkQualityLevel <= MAX_NETWORK_QUALITY_LEVEL;

  const isLowQuality = hasBlackScreen || isQualityLevelLow;

  return { isLowQuality };
}
